import React from 'react';
import { Box, Typography, Chip, Stack, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as Styles from '../../assets/styles/appStyles';
import SidebarLayout from '../../common/SidebarLayout';

interface FacilitySidebarProps {
  open: boolean;
  onClose: () => void;
  facilityData: any;
  topOffset?: number;
}

const FacilitySidebar: React.FC<FacilitySidebarProps> = ({
  open,
  onClose,
  facilityData,
  topOffset = 64,
}) => {
  const navigate = useNavigate();
  if (!facilityData) return null;

  const handleMoreDetails = () =>
    navigate(`/entity/details/${facilityData.entityId}`);
  const recordTypes = Object.keys(facilityData.associatedEntities || {});

  const renderBulletList = (items: string[]) => (
    <ul style={{ margin: 0, paddingLeft: '20px' }}>
      {items.map((item, idx) => (
        <li
          key={idx}
          style={{ marginBottom: '2px', fontWeight: '600', fontSize: '17px' }}
        >
          {item}
        </li>
      ))}
    </ul>
  );

  return (
    <SidebarLayout
      open={open}
      onClose={onClose}
      topOffset={topOffset}
      title={facilityData.facility}
      onMoreDetails={handleMoreDetails}
    >
      {/* Details Grid */}
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Address Fields */}
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={Styles.labelTitle}>
              Address:
            </Typography>
            <Typography variant="body2" sx={Styles.sidebarLabelText}>
              {facilityData.address?.addressDetails || '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={Styles.labelTitle}>
              City:
            </Typography>
            <Typography variant="body2" sx={Styles.sidebarLabelText}>
              {facilityData.address?.city || '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={Styles.labelTitle}>
              State:
            </Typography>
            <Typography variant="body2" sx={Styles.sidebarLabelText}>
              {facilityData.address?.state || '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={Styles.labelTitle}>
              Postcode:
            </Typography>
            <Typography variant="body2" sx={Styles.sidebarLabelText}>
              {facilityData.address?.postCode || '-'}
            </Typography>
          </Grid>

          {/* Email */}
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={Styles.labelTitle}>
              Email:
            </Typography>
            {facilityData.emailAddresses?.length ? (
              renderBulletList(
                facilityData.emailAddresses.map((e: any) => e.email),
              )
            ) : (
              <Typography variant="body2" sx={Styles.sidebarLabelText}>
                -
              </Typography>
            )}
          </Grid>

          {/* Phone/Fax */}
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={Styles.labelTitle}>
              Phone/Fax:
            </Typography>
            {facilityData.phoneNumbers?.length ? (
              renderBulletList(
                facilityData.phoneNumbers.map(
                  (p: any) =>
                    `${p.phoneNumber} (${p.phoneType}${
                      p.isPrimary ? ' • Primary' : ''
                    })`,
                ),
              )
            ) : (
              <Typography variant="body2" sx={Styles.sidebarLabelText}>
                -
              </Typography>
            )}
          </Grid>

          {/* Contacts: Name, Phone, Email */}
          {facilityData.contacts?.length > 0 && (
            <>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" sx={Styles.labelTitle}>
                  Contact Name
                </Typography>
                {renderBulletList(
                  facilityData.contacts.map(
                    (c: any) => `${c.name}${c.isPrimary ? ' • Primary' : ''}`,
                  ),
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" sx={Styles.labelTitle}>
                  Contact Phone
                </Typography>
                {renderBulletList(
                  facilityData.contacts.map((c: any) => c.phoneNumber || '-'),
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" sx={Styles.labelTitle}>
                  Contact Email
                </Typography>
                {renderBulletList(
                  facilityData.contacts.map((c: any) => c.email || '-'),
                )}
              </Grid>
            </>
          )}

          {/* Record Types */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={Styles.labelTitle}>
              Record Types:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1 }}>
              {recordTypes.length > 0 ? (
                recordTypes.map((type) => (
                  <Chip
                    key={type}
                    label={type}
                    size="small"
                    sx={{
                      bgcolor: '#f9f0e5',
                      color: '#f5831f',
                      fontWeight: 'bold',
                    }}
                  />
                ))
              ) : (
                <Typography variant="body2">No records</Typography>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </SidebarLayout>
  );
};

export default FacilitySidebar;
